/**
 * FE-PR-20 — sign-in screen orchestration hook.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { clearOrdersAuthGateIntent, isBottomNavAuthChromePath } from '../../../app/bottomNavActiveTab.js';
import { useAccountMe, useAccountMemberships } from '../../account/accountApi.js';
import { useTenantCode } from '../../../shared/tenant/TenantProvider.js';
import { useCustomerSession } from '../../../shared/session/CustomerSessionProvider.js';
import { resolveOidcLoginReturnTo } from '../../../shared/session/returnTarget.js';
import { isPlatformChromeTenant } from '../../../app/platformChromeTenant.js';
import { useGuestAuthScreenDefense } from '../routing/useGuestAuthScreenDefense.js';
import { isGuestAuthPath } from '../routing/isGuestAuthPath.js';
import { useCookieConsent } from '../../consent/CookieConsentProvider.js';
import { parkPostPayAuthReturnTarget } from '../../post-pay/parkPostPayAuthReturn.js';
import {
  platformRedirectToOidcLogin,
  resolveSocialAuthVisibility,
  useContactPolicy,
  useLoginWithPassword,
  useOidcLoginStatus,
  useRequestOtp,
  useVerifyOtp,
  type RequestOtpResponse,
} from '../authApi.js';
import {
  ANALYTICS_UNIVERSAL_EVENTS,
  resolveContactIdentifierCopyKind,
  resolveContactIdentifierInputMode,
  resolvePasswordIdentifierCopyKind,
} from 'pi-kiosk-shared';
import type { AnalyticsEventName } from 'pi-kiosk-shared/analyticsEvents';
import {
  contactIdentifierHelperKey,
  contactIdentifierLabelKey,
  contactIdentifierPlaceholderKey,
  passwordHintKey,
  passwordIdentifierLabelKey,
  passwordIdentifierPlaceholderKey,
} from '../contactPolicyCopy.js';
import { CustomerAuthErrorCodes, isApiErrorCode } from '../authErrorCodes.js';
import {
  DEFAULT_REGISTRATION_CONSENTS,
  type RegistrationConsentsFormState,
} from '../authConsents.js';
import { shouldShowOidcStartConsents } from '../oidcConsentPolicy.js';
import {
  readOidcStartConsents,
} from '../oidcStartConsentsStorage.js';
import { resolveOidcRecoveryMessage } from '../resolveOidcRecoveryMessage.js';
import { toCustomerFacingErrorMessage } from '../../../shared/errors/toCustomerFacingErrorMessage.js';
import { resolveCustomerSignUpPathWithOidc } from '../customerAuthPaths.js';
import { useRuntimeConfig } from '../../../shared/providers/RuntimeConfigProvider.js';
import { useTurnstileExecute } from 'pi-kiosk-shared/ui';
import { useCustomerAnalytics } from '../../../shared/analytics/CustomerAnalyticsProvider.js';
import { buildScreenMetadata } from 'pi-kiosk-shared/analytics/metadataBuilders';
import { createSignInScreenHandlers } from '../handlers/signInScreenHandlers.js';
import {
  useRememberMeFieldVisibility,
} from '../useRememberMeFieldVisibility.js';
import {
  resolveSignInChannelHint,
} from '../presenters/signInScreenPresenter.js';
import { useCustomerObservability } from '../../../shared/observability/CustomerObservabilityContext.js';
import { oidcLog } from '../logging.js';

export interface UseSignInScreenInput {
  /** After full session land — may fire before React session re-render; prefer the passed customerId. */
  readonly onSignedIn?: (customerId: number) => void;
  readonly heading?: string;
  readonly helperText?: string;
}

export interface SignInLocationState {
  emailVerified?: boolean;
  credentialsSet?: boolean;
}

function initialOtpConsentsFromStorage(): RegistrationConsentsFormState {
  const stored = readOidcStartConsents();
  if (stored === null) {
    return DEFAULT_REGISTRATION_CONSENTS;
  }
  return {
    ...DEFAULT_REGISTRATION_CONSENTS,
    termsAccepted: stored.terms,
    privacyAccepted: stored.privacy,
  };
}

export function useSignInScreen(input: UseSignInScreenInput = {}) {
  const { onSignedIn, heading, helperText } = input;
  const { t } = useTranslation('customer');
  const tenantCode = useTenantCode();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { session } = useCustomerSession();
  const accountMe = useAccountMe(session !== null);
  const memberships = useAccountMemberships(session !== null);

  /**
   * Defense in depth for dedicated guest-auth routes only.
   * Prefer RequireGuestAuth so the form never mounts; this covers accidental
   * direct mounts on `/sign-in`. Inline embeds (PostPay / Refund / CardLink /
   * QR under R1) intentionally skip this — they gate on isSignedInNavChrome.
   * G5: OIDC land hold parity with RequireGuestAuth.
   */
  const { guestAuthBlocked } = useGuestAuthScreenDefense();

  /**
   * G3/G4/G8 — visit/OIDC-start org from PATH SSOT only (never login `?tenant=`).
   * Platform chrome `/sign-in` → null; path `/{tenant}/sign-in` → that slug.
   */
  const oidcStartTenantCode = useMemo((): string | null => {
    if (!isPlatformChromeTenant(tenantCode)) {
      return tenantCode;
    }
    return null;
  }, [tenantCode]);

  /**
   * Prospect helper banner only (sign-up link). Must NOT preempt Google/Apple before IdP —
   * recovery is post-fail LEGAL_CONSENTS_REQUIRED / oidc_consent_required → Registrace.
   */
  const currentTenantProspect =
    oidcStartTenantCode !== null &&
    (memberships.data?.memberships.some(
      (m) =>
        m.tenantCode === oidcStartTenantCode &&
        m.membershipStatus === 'PROSPECT',
    ) ??
      false);
  const resolvedHeading = heading ?? t('auth.heading');
  const contactPolicy = useContactPolicy();
  const identifierCopyKind = resolveContactIdentifierCopyKind(contactPolicy.data);
  const passwordCopyKind = resolvePasswordIdentifierCopyKind(contactPolicy.data);
  const resolvedHelperText =
    helperText ?? t(contactIdentifierHelperKey(identifierCopyKind));
  const identifierLabel = t(contactIdentifierLabelKey(identifierCopyKind));
  const identifierPlaceholder = t(
    contactIdentifierPlaceholderKey(identifierCopyKind),
  );
  const passwordIdentifierLabel = t(passwordIdentifierLabelKey(passwordCopyKind));
  const passwordIdentifierPlaceholder = t(
    passwordIdentifierPlaceholderKey(passwordCopyKind),
  );

  const [oidcPlatformRegisteredHint, setOidcPlatformRegisteredHint] = useState(false);

  const postOnboardingHint = useMemo(():
    | 'credentialsSet'
    | 'emailVerified'
    | 'oidcPlatformRegistered'
    | null => {
    const state = location.state as SignInLocationState | null;
    if (state?.credentialsSet === true) {
      return 'credentialsSet';
    }
    if (oidcPlatformRegisteredHint) {
      return 'oidcPlatformRegistered';
    }
    if (state?.emailVerified === true) {
      return 'emailVerified';
    }
    return null;
  }, [location.state, oidcPlatformRegisteredHint]);

  const [authMethod, setAuthMethod] = useState<'otp' | 'password'>('password');
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const showRememberMe = useRememberMeFieldVisibility();
  const [identifierError, setIdentifierError] = useState<string | undefined>(undefined);
  const [passwordError, setPasswordError] = useState<string | undefined>(undefined);
  const [codeError, setCodeError] = useState<string | undefined>(undefined);
  /** G4 — API/form-level banner (ForgotPassword pattern); never dual with FormErrorSummary. */
  const [formError, setFormError] = useState<string | undefined>(undefined);
  const [deliveryErrorCode, setDeliveryErrorCode] = useState<
    | typeof CustomerAuthErrorCodes.OTP_DELIVERY_FAILED
    | typeof CustomerAuthErrorCodes.SMS_NOT_AVAILABLE
    | undefined
  >(undefined);
  const [credentialsNotConfigured, setCredentialsNotConfigured] = useState(false);
  const [emailNotVerified, setEmailNotVerified] = useState(false);
  const [accountLocked, setAccountLocked] = useState(false);
  const [accountLockedUntil, setAccountLockedUntil] = useState<string | undefined>(undefined);
  const [showOtpConsents, setShowOtpConsents] = useState(false);
  const [showPasswordConsents, setShowPasswordConsents] = useState(false);
  const [otpConsents, setOtpConsents] = useState<RegistrationConsentsFormState>(
    initialOtpConsentsFromStorage,
  );
  const [consentFieldErrors, setConsentFieldErrors] = useState<Record<string, string>>({});
  const [challenge, setChallenge] = useState<RequestOtpResponse | null>(null);
  const [oidcRecoveryMessage, setOidcRecoveryMessage] = useState<string | undefined>(
    undefined,
  );
  const [consumedOidcErrorParam, setConsumedOidcErrorParam] = useState<string | null>(null);

  const consent = useCookieConsent();
  const loginPassword = useLoginWithPassword();
  const requestOtp = useRequestOtp();
  const verifyOtp = useVerifyOtp();
  const oidcStatus = useOidcLoginStatus();
  const runtimeConfig = useRuntimeConfig();
  const socialAuth = resolveSocialAuthVisibility(oidcStatus.data);
  const turnstile = useTurnstileExecute(runtimeConfig.apiBaseUrl);
  const analytics = useCustomerAnalytics();
  const { setLastAuthProvider } = useCustomerObservability();
  const authFlowStartedRef = useRef(false);
  const sessionRef = useRef(session);

  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  /**
   * Guest Objednávky auth-gate: clear sticky orders intent when leaving sign-in
   * without success (back, bottom-nav away, explicit cancel). Defer one microtask
   * so sign-in → sign-up / forgot-password (still auth chrome) does not wipe intent.
   */
  useEffect(() => {
    if (!isGuestAuthPath(location.pathname)) {
      return;
    }
    return () => {
      if (sessionRef.current !== null) {
        return;
      }
      queueMicrotask(() => {
        if (sessionRef.current !== null) {
          return;
        }
        if (isBottomNavAuthChromePath(window.location.pathname, tenantCode)) {
          return;
        }
        clearOrdersAuthGateIntent();
      });
    };
  }, [location.pathname, tenantCode]);

  const oidcErrorParam = searchParams.get('error');
  const oidcRecoveryFromUrl = resolveOidcRecoveryMessage(t, oidcErrorParam, {
    authenticated: false,
  });
  const isOidcConsentsRequiredError =
    oidcErrorParam === 'oidc_consent_required' || oidcErrorParam === 'CONSENTS_REQUIRED';
  // URL → local recovery; consent-required navigates to register in useEffect (no ticks).
  if (
    oidcErrorParam !== null &&
    oidcErrorParam !== consumedOidcErrorParam &&
    oidcRecoveryFromUrl !== undefined
  ) {
    setConsumedOidcErrorParam(oidcErrorParam);
    if (!isOidcConsentsRequiredError) {
      setOidcRecoveryMessage(oidcRecoveryFromUrl);
    }
  }
  if (searchParams.get('oidcPlatformRegistered') === '1' && !oidcPlatformRegisteredHint) {
    setOidcPlatformRegisteredHint(true);
  }

  useEffect(() => {
    if (authFlowStartedRef.current) {
      return;
    }
    authFlowStartedRef.current = true;
    analytics.track({
      eventName: ANALYTICS_UNIVERSAL_EVENTS.AUTH_FLOW_STARTED,
      screenName: 'sign_in',
      metadata: buildScreenMetadata({ screenName: 'sign_in' }),
    });
  }, [analytics]);

  useEffect(() => {
    if (!oidcStatus.isError) {
      return;
    }
    oidcLog.error('OIDC status load failed', oidcStatus.error, {
      operation: 'oidcStatus',
      tenantCode,
    });
  }, [oidcStatus.error, oidcStatus.isError, tenantCode]);

  useEffect(() => {
    if (oidcErrorParam === null || oidcRecoveryFromUrl === undefined) {
      return;
    }
    // G4: CONSENTS_REQUIRED / oidc_consent_required → register (ticks are register-only).
    if (
      oidcErrorParam === 'oidc_consent_required' ||
      oidcErrorParam === 'CONSENTS_REQUIRED'
    ) {
      const oidcParam = searchParams.get('oidc');
      const provider =
        oidcParam === 'google' || oidcParam === 'apple' ? oidcParam : null;
      // G8 — Registrace handoff from PATH SSOT only (never login ?tenant=).
      navigate(resolveCustomerSignUpPathWithOidc(tenantCode, provider), {
        replace: true,
      });
      return;
    }
    const next = new URLSearchParams(searchParams);
    next.delete('error');
    setSearchParams(next, { replace: true });
  }, [
    oidcErrorParam,
    oidcRecoveryFromUrl,
    searchParams,
    setSearchParams,
    navigate,
    tenantCode,
  ]);

  useEffect(() => {
    if (searchParams.get('oidcPlatformRegistered') !== '1') {
      return;
    }
    const next = new URLSearchParams(searchParams);
    next.delete('oidcPlatformRegistered');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const handlers = useMemo(
    () =>
      createSignInScreenHandlers({
        t,
        tenantCode,
        navigate,
        analytics,
        turnstile,
        requestOtp,
        verifyOtp,
        loginPassword,
        accountHasLegalGrant: accountMe.data?.hasLegalGrant,
        showRememberMe,
        passwordPhoneLookupPolicy: {
          phoneAllowed: contactPolicy.data?.phoneAllowed === true,
        },
        onSignedIn,
        identifier,
        password,
        code,
        rememberMe,
        challenge,
        showOtpConsents,
        showPasswordConsents,
        otpConsents,
        setIdentifierError,
        setPasswordError,
        setCodeError,
        setFormError,
        setDeliveryErrorCode,
        setCredentialsNotConfigured,
        setEmailNotVerified,
        setAccountLocked,
        setAccountLockedUntil,
        setShowOtpConsents,
        setShowPasswordConsents,
        setConsentFieldErrors,
        setChallenge,
        setOtpConsents,
        setLastAuthProvider,
      }),
    [
      t,
      tenantCode,
      navigate,
      analytics,
      turnstile,
      requestOtp,
      verifyOtp,
      loginPassword,
      accountMe.data?.hasLegalGrant,
      showRememberMe,
      contactPolicy.data?.phoneAllowed,
      onSignedIn,
      identifier,
      password,
      code,
      rememberMe,
      challenge,
      showOtpConsents,
      showPasswordConsents,
      otpConsents,
      setIdentifierError,
      setPasswordError,
      setCodeError,
      setFormError,
      setDeliveryErrorCode,
      setCredentialsNotConfigured,
      setEmailNotVerified,
      setAccountLocked,
      setAccountLockedUntil,
      setShowOtpConsents,
      setShowPasswordConsents,
      setConsentFieldErrors,
      setChallenge,
      setOtpConsents,
      setLastAuthProvider,
    ],
  );

  const channelHint = resolveSignInChannelHint({
    t,
    authMethod,
    challenge,
    identifier,
    resolvedHelperText,
    passwordHintKey: passwordHintKey(passwordCopyKind),
  });
  const identifierInputMode = resolveContactIdentifierInputMode({
    copyKind: identifierCopyKind,
    rawIdentifier: identifier,
  });
  const showPageTurnstile = turnstile.required && socialAuth.showAny;

  const resetOtpChallenge = (): void => {
    setChallenge(null);
    setCode('');
    setCodeError(undefined);
    setFormError(undefined);
    setShowOtpConsents(false);
    setOtpConsents(DEFAULT_REGISTRATION_CONSENTS);
    setAccountLocked(false);
    setAccountLockedUntil(undefined);
  };

  const switchToOtpTab = (): void => {
    setAuthMethod('otp');
    setPassword('');
    setPasswordError(undefined);
    setFormError(undefined);
    setCredentialsNotConfigured(false);
    setAccountLocked(false);
    setAccountLockedUntil(undefined);
    setRememberMe(false);
  };

  const switchToPasswordTab = (): void => {
    setAuthMethod('password');
    resetOtpChallenge();
    setRememberMe(false);
  };

  // Sign-in never shows OIDC consent ticks (register-only).
  const showOidcStartConsents =
    socialAuth.showAny && shouldShowOidcStartConsents();

  const handleOidcLogin = async (provider: 'google' | 'apple'): Promise<void> => {
    setOidcRecoveryMessage(undefined);
    setLastAuthProvider(provider);
    setConsentFieldErrors({});
    analytics.track({
      eventName: 'auth_provider_clicked' as AnalyticsEventName,
      flow: 'RETAIL',
      screenName: 'sign_in',
      metadata: { auth_provider: provider },
    });

    // Login is TENANTLESS: never carry path/?tenant= into IdP. Register-only uses org.
    // Recovery tenant is visit chrome only (post LEGAL_CONSENTS_REQUIRED → Registrace).
    // IdP: same-window full-document redirect (common Google/Apple OAuth pattern).
    const recoveryTenant = oidcStartTenantCode ?? tenantCode;

    try {
      const token = await turnstile.execute();
      if (turnstile.required && (token === undefined || token.length === 0)) {
        oidcLog.warn('OIDC start blocked: Turnstile token missing', {
          operation: 'oidcLogin',
          tenantCode: '__platform__',
          provider,
        });
        setOidcRecoveryMessage(t('account.stepUp.turnstileRequired'));
        return;
      }
      // G1 — park current location (embedded PostPay checkout/donate) before
      // IdP redirect so FE restore remounts payment after land. Auto-start
      // `?oidc=` uses this same path via handleOidcLoginRef.
      const path = `${location.pathname}${location.search}`;
      parkPostPayAuthReturnTarget(path, tenantCode);
      await platformRedirectToOidcLogin(
        provider,
        resolveOidcLoginReturnTo(tenantCode),
        token,
      );
    } catch (error) {
      oidcLog.error('OIDC login redirect failed', error, {
        operation: 'oidcLogin',
        tenantCode: '__platform__',
        provider,
      });
      if (
        isApiErrorCode(error as Error, CustomerAuthErrorCodes.LEGAL_CONSENTS_REQUIRED) ||
        isApiErrorCode(error as Error, 'CONSENTS_REQUIRED') ||
        isApiErrorCode(error as Error, 'oidc_consent_required')
      ) {
        // G4: Login must not collect ticks — navigate to register with provider.
        navigate(resolveCustomerSignUpPathWithOidc(recoveryTenant, provider), {
          replace: true,
        });
        return;
      }
      const fromApi = resolveOidcRecoveryMessage(
        t,
        error instanceof Error && 'code' in error
          ? String((error as { code?: unknown }).code ?? '')
          : null,
        { authenticated: false },
      );
      if (fromApi !== undefined) {
        setOidcRecoveryMessage(fromApi);
      } else {
        setOidcRecoveryMessage(
          toCustomerFacingErrorMessage(error, {
            fallback: t('auth.oidc.error.unavailable'),
          }),
        );
      }
      turnstile.resetTurnstile();
    }
  };

  const handleOidcLoginRef = useRef(handleOidcLogin);
  useEffect(() => {
    handleOidcLoginRef.current = handleOidcLogin;
  });

  const oidcAutoStartRef = useRef(false);
  useEffect(() => {
    if (oidcAutoStartRef.current) {
      return;
    }
    const oidc = searchParams.get('oidc');
    if (oidc !== 'google' && oidc !== 'apple') {
      return;
    }
    // Auto-start always uses platformRedirectToOidcLogin (tenantless login).
    if (oidcStatus.isLoading || oidcStatus.isPending) {
      return;
    }
    const visible = resolveSocialAuthVisibility(oidcStatus.data);
    if ((oidc === 'google' && !visible.showGoogle) || (oidc === 'apple' && !visible.showApple)) {
      return;
    }
    oidcAutoStartRef.current = true;
    const next = new URLSearchParams(searchParams);
    next.delete('oidc');
    setSearchParams(next, { replace: true });
    void handleOidcLoginRef.current(oidc);
  }, [
    tenantCode,
    searchParams,
    oidcStatus.isLoading,
    oidcStatus.isPending,
    oidcStatus.data,
    setSearchParams,
  ]);

  // Sign-in never surfaces OIDC consent handoff errors (register-only ticks).
  const oidcHandoffConsentErrors: Record<string, string> = {};

  const mergedConsentFieldErrors = {
    ...consentFieldErrors,
    ...oidcHandoffConsentErrors,
  };

  return {
    t,
    tenantCode,
    oidcStartTenantCode,
    guestAuthBlocked,
    resolvedHeading,
    channelHint,
    identifierLabel,
    identifierPlaceholder,
    identifierInputMode,
    passwordIdentifierLabel,
    passwordIdentifierPlaceholder,
    currentTenantProspect,
    postOnboardingHint,
    authMethod,
    identifier,
    setIdentifier,
    password,
    setPassword,
    code,
    setCode,
    rememberMe,
    setRememberMe,
    showRememberMe,
    identifierError,
    passwordError,
    codeError,
    formError,
    deliveryErrorCode,
    credentialsNotConfigured,
    emailNotVerified,
    accountLocked,
    accountLockedUntil,
    showOtpConsents,
    showPasswordConsents,
    otpConsents,
    setOtpConsents,
    consentFieldErrors: mergedConsentFieldErrors,
    challenge,
    consent,
    turnstile,
    socialAuth,
    showOidcStartConsents,
    showPageTurnstile,
    oidcRecoveryMessage,
    requestOtp,
    verifyOtp,
    loginPassword,
    ...handlers,
    resetOtpChallenge,
    switchToOtpTab,
    switchToPasswordTab,
    handleOidcLogin,
    setCredentialsNotConfigured,
    setPasswordError,
    setAuthMethod,
  };
}

export type SignInScreenViewProps = ReturnType<typeof useSignInScreen>;
